import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
  ArrowLeft,
  Send,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from 'lucide-react';
import { useFines } from 'context/FinesContext';
import { Fine } from 'utils/types';
import TableSkeleton from 'components/common/skeletons/TableSkeleton';
import useOfficerNotifyFine from 'hooks/fines/useOfficerNotifyFine';
import { Bounce, toast } from 'react-toastify';

// ─── SMS badge ────────────────────────────────────────────────────────────────

const SMS_CONFIG: Record<
  string,
  { label: string; className: string; icon: string }
> = {
  S: {
    label: 'Sent',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: '✅',
  },
  P: {
    label: 'Pending',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: '⏳',
  },
  F: {
    label: 'Failed',
    className: 'bg-red-50 text-red-700 border-red-200',
    icon: '⚠️',
  },
};

const SmsBadge: React.FC<{ status: string | null }> = ({ status }) => {
  const cfg = status ? SMS_CONFIG[status] : null;
  if (!cfg) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.className}`}
    >
      {cfg.icon} {cfg.label}
    </span>
  );
};

// ─── Filter input ─────────────────────────────────────────────────────────────

const FilterInput: React.FC<{
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
}> = ({ placeholder, value, onChange, onClear }) => (
  <div className="relative">
    <Search
      size={13}
      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
    />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-8 pr-7 py-1.5 text-xs rounded-lg border border-gray-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition w-40"
    />
    {value && (
      <button
        onClick={onClear}
        className="absolute right-2 top-1/2 -translate-y-1/2"
      >
        <X size={12} className="text-gray-400 hover:text-gray-600" />
      </button>
    )}
  </div>
);

// ─── SMS filter select ────────────────────────────────────────────────────────

const SmsFilterSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="py-1.5 px-2 text-xs rounded-lg border border-gray-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition text-gray-700 bg-white"
  >
    <option value="">All SMS</option>
    <option value="S">✅ Sent</option>
    <option value="P">⏳ Pending</option>
    <option value="F">⚠️ Failed</option>
  </select>
);

// ─── Main component ───────────────────────────────────────────────────────────

const NTSAFinesList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    fines,
    isLoading,
    currentPage,
    totalPages,
    totalEntries,
    pageSize,
    goToPage,
    handleFilter,
    handlePageSizeChange,
  } = useFines();

  // Seed local filter state from URL params (set by dashboard card clicks)
  const [filters, setFilters] = useState({
    ticketNumber: searchParams.get('ticketNumber') || '',
    vehicleRegistrationNumber:
      searchParams.get('vehicleRegistrationNumber') || '',
    location: searchParams.get('location') || '',
    smsStatus: searchParams.get('smsStatus') || '',
    dateCapturedFrom: searchParams.get('dateCapturedFrom') || '',
    dateCapturedTo: searchParams.get('dateCapturedTo') || '',
  });

  // On mount, apply any URL-seeded filters to the context
  useEffect(() => {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) handleFilter(key, value);
    });
  }, []);

  const applyFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    handleFilter(key, value);
  };

  const clearFilter = (key: string) => applyFilter(key, '');

  const { mutateAsync: notifyFineMutation } = useOfficerNotifyFine();
  const [sendingId, setSendingId] = useState<number | null>(null);

  const handleSend = async (fine: Fine) => {
    setSendingId(fine.id);
    await notifyFineMutation({ fineId: fine.id.toString() })
      .then(() =>
        toast.success('SMS notification sent successfully', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'light',
          transition: Bounce,
        }),
      )
      .catch(() =>
        toast.error('Error sending SMS notification', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'light',
          transition: Bounce,
        }),
      )
      .finally(() => setSendingId(null));
  };

  const smsLabel =
    filters.smsStatus === 'S'
      ? ' · SMS Sent'
      : filters.smsStatus === 'F'
        ? ' · SMS Failed'
        : filters.smsStatus === 'P'
          ? ' · SMS Pending'
          : '';

  return (
    <div className="relative">
      <div className="absolute inset-0 z-10 grid grid-cols-4 gap-8 opacity-[0.1] pointer-events-none">
        {Array.from({ length: 50 }).map((_, idx) => (
          <div key={idx} className="flex items-center justify-center">
            <img
              src="/ntsa-logo.svg"
              alt=""
              className="w-16 h-16 object-contain"
            />
          </div>
        ))}
      </div>
      <div className="relative z-0 bg-gray-50">
        {/* ── Header ── */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className=" flex items-center gap-4">
            <button
              onClick={() => navigate('/fines/dashboard')}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={16} />
              Dashboard
            </button>
            <div className="h-4 w-px bg-gray-200" />
            <div>
              <h1 className="text-base font-semibold text-gray-900">
                Offences List
              </h1>
              <p className="text-xs text-gray-400">
                {totalEntries.toLocaleString()} record
                {totalEntries !== 1 ? 's' : ''}
                {smsLabel}
              </p>
            </div>
          </div>
        </div>

        <div className=" px-6 py-6">
          {/* ── Filter row ── */}
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Filter
            </span>
            <FilterInput
              placeholder="Ticket No."
              value={filters.ticketNumber}
              onChange={(v) => applyFilter('ticketNumber', v)}
              onClear={() => clearFilter('ticketNumber')}
            />
            <FilterInput
              placeholder="Vehicle Reg."
              value={filters.vehicleRegistrationNumber}
              onChange={(v) =>
                applyFilter('vehicleRegistrationNumber', v.toUpperCase())
              }
              onClear={() => clearFilter('vehicleRegistrationNumber')}
            />
            <FilterInput
              placeholder="Location"
              value={filters.location}
              onChange={(v) => applyFilter('location', v)}
              onClear={() => clearFilter('location')}
            />
            <SmsFilterSelect
              value={filters.smsStatus}
              onChange={(v) => applyFilter('smsStatus', v)}
            />

            {/* Page size */}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-gray-400">Rows</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="py-1.5 px-2 text-xs rounded-lg border border-gray-200 outline-none focus:border-blue-400 bg-white text-gray-700"
              >
                {[10, 20, 50, 100].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Table ── */}
          {isLoading ? (
            <TableSkeleton />
          ) : fines.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
              <p className="text-gray-400 text-sm">
                No offences found for the selected filters.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* NTSA watermark */}
              <div className="relative">
                <div className="absolute inset-0 z-0 grid grid-cols-5 gap-4 opacity-5 pointer-events-none overflow-hidden">
                  {Array.from({ length: 30 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-center p-4"
                    >
                      <img
                        src="/ntsa-logo.svg"
                        alt=""
                        className="w-16 h-16 object-contain"
                      />
                    </div>
                  ))}
                </div>

                <div className="relative z-10 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        {[
                          'Ticket No.',
                          'Date Captured',
                          'Vehicle Reg.',
                          'Location',
                          'SMS Status',
                          '',
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {fines.map((fine) => (
                        <tr
                          key={fine.id}
                          className="hover:bg-blue-50/40 transition-colors"
                        >
                          <td className="px-4 py-3.5 font-mono text-xs text-gray-700 whitespace-nowrap">
                            {String(fine.ticketNumber).slice(-6).toUpperCase()}
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-600 whitespace-nowrap">
                            {moment(fine.dateCaptured).format(
                              'D MMM YYYY, h:mm a',
                            )}
                          </td>
                          <td className="px-4 py-3.5 font-medium text-xs text-gray-800 whitespace-nowrap">
                            {fine.vehicleRegistrationNumber}
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-600 max-w-[180px] truncate">
                            {fine.location}
                          </td>
                          <td className="px-4 py-3.5">
                            <SmsBadge status={fine.smsStatus} />
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  navigate(`/fines/single/${fine.ticketNumber}`)
                                }
                                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap"
                              >
                                View Details
                              </button>
                              {fine.smsStatus !== 'S' && (
                                <button
                                  onClick={() => handleSend(fine)}
                                  disabled={sendingId === fine.id}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                  <Send size={11} />
                                  {sendingId === fine.id
                                    ? 'Sending…'
                                    : 'Send SMS'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Pagination ── */}
              <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-gray-400">
                  Page {currentPage} of {totalPages} ·{' '}
                  {totalEntries.toLocaleString()} total
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const start = Math.max(1, currentPage - 2);
                    const page = start + i;
                    if (page > totalPages) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                          page === currentPage
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-50 border border-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NTSAFinesList;
